import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Visibility,
} from '@mui/icons-material';
import { useAllRedemptions } from '../../hooks/useRedemptions';
import { useLenders } from '../../hooks/useLenders';
import { approveRedemption, rejectRedemption, completeRedemption } from '../../services/redemptionService';
import { Redemption } from '../../types';
import { formatCurrency } from '../../utils/navCalculations';
import { format } from 'date-fns';

const RedemptionsQueue: React.FC = () => {
  const { redemptions, loading, error } = useAllRedemptions(true);
  const { lenders } = useLenders(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');

  const getLenderName = (lenderId: string): string => {
    const lender = lenders.find(l => l.id === lenderId);
    return lender?.fullName || 'Unknown';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'processing': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <HourglassEmpty />;
      case 'processing': return <CheckCircle />;
      case 'completed': return <CheckCircle />;
      case 'cancelled': return <Cancel />;
      default: return <HourglassEmpty />;
    }
  };

  const handleApprove = async (redemption: Redemption) => {
    if (!window.confirm(`Approve redemption for ${getLenderName(redemption.lenderId)}?\n\nAmount: ${formatCurrency(redemption.redemptionValue)}`)) {
      return;
    }

    setProcessingId(redemption.id);
    try {
      await approveRedemption(redemption.id);
      setSuccessMessage(`Redemption approved for ${getLenderName(redemption.lenderId)}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error approving:', error);
      alert('Failed to approve redemption');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (redemption: Redemption) => {
    const reason = window.prompt(`Enter reason for rejecting redemption for ${getLenderName(redemption.lenderId)}:`);
    if (!reason) return;

    setProcessingId(redemption.id);
    try {
      await rejectRedemption(redemption.id, reason);
      setSuccessMessage(`Redemption rejected for ${getLenderName(redemption.lenderId)}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error rejecting:', error);
      alert('Failed to reject redemption');
    } finally {
      setProcessingId(null);
    }
  };

  const handleComplete = async (redemption: Redemption) => {
    if (!window.confirm(`Mark redemption as completed?\n\nLender: ${getLenderName(redemption.lenderId)}\nAmount: ${formatCurrency(redemption.redemptionValue)}`)) {
      return;
    }

    setProcessingId(redemption.id);
    try {
      await completeRedemption(redemption.id);
      setSuccessMessage(`Redemption completed for ${getLenderName(redemption.lenderId)}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error completing:', error);
      alert('Failed to complete redemption');
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Error loading redemptions: {error}
      </Alert>
    );
  }

  const pendingRedemptions = redemptions.filter(r => r.status === 'pending' || r.status === 'processing');
  const completedRedemptions = redemptions.filter(r => r.status === 'completed' || r.status === 'cancelled');

  return (
    <Box>
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Pending/Processing Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Pending Redemptions ({pendingRedemptions.length})
          </Typography>

          {pendingRedemptions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No pending redemptions
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Lender</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Requested</TableCell>
                    <TableCell>Redemption Date</TableCell>
                    <TableCell>Early?</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingRedemptions.map((redemption) => (
                    <TableRow key={redemption.id}>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(redemption.status)}
                          label={redemption.status.toUpperCase()}
                          color={getStatusColor(redemption.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{getLenderName(redemption.lenderId)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(redemption.redemptionValue)}
                      </TableCell>
                      <TableCell>
                        {format(new Date(redemption.requestDate), 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell>
                        {format(new Date(redemption.redemptionDate), 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell>
                        {redemption.isEarlyRedemption ? (
                          <Chip label="Yes" color="warning" size="small" />
                        ) : (
                          <Chip label="No" color="success" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {redemption.status === 'pending' && (
                            <>
                              <IconButton
                                color="success"
                                size="small"
                                onClick={() => handleApprove(redemption)}
                                disabled={processingId === redemption.id}
                                title="Approve"
                              >
                                <CheckCircle />
                              </IconButton>
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleReject(redemption)}
                                disabled={processingId === redemption.id}
                                title="Reject"
                              >
                                <Cancel />
                              </IconButton>
                            </>
                          )}
                          {redemption.status === 'processing' && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleComplete(redemption)}
                              disabled={processingId === redemption.id}
                            >
                              Complete
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
       </CardContent>
      </Card>

      {/* Completed/Cancelled Section */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Completed Redemptions ({completedRedemptions.length})
          </Typography>

          {completedRedemptions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No completed redemptions
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Lender</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Requested</TableCell>
                    <TableCell>Completed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {completedRedemptions.map((redemption) => (
                    <TableRow key={redemption.id}>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(redemption.status)}
                          label={redemption.status.toUpperCase()}
                          color={getStatusColor(redemption.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{getLenderName(redemption.lenderId)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(redemption.redemptionValue)}
                      </TableCell>
                      <TableCell>
                        {format(new Date(redemption.requestDate), 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell>
                        {redemption.paymentDate 
                          ? format(new Date(redemption.paymentDate), 'MMM dd, yyyy')
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default RedemptionsQueue;
